import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Typography,
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';
import { getCommunicationAttachmentBlob } from '../../api/communicationApi';
import T from '../common/T';

export default function ChatImageAttachment({ attachment, messageId, attachmentIndex, isOutbound = false }) {
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const [imageType, setImageType] = useState('');
  const filename = attachment.filename || `Bild ${attachmentIndex + 1}`;

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl;
    setImageUrl('');
    setLoading(true);
    setFailed(false);
    setOpen(false);

    getCommunicationAttachmentBlob(messageId, attachmentIndex, { signal: controller.signal })
      .then((blob) => {
        if (controller.signal.aborted) return;
        if (!blob.size) throw new Error('Empty image');
        setImageType(blob.type);
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [messageId, attachmentIndex, attempt]);

  const extension = { 'image/jpeg': '.jpg', 'image/png': '.png', 'image/gif': '.gif', 'image/webp': '.webp', 'image/heic': '.heic' }[imageType] || '';
  const downloadFilename = /\.[a-z\d]{2,5}$/i.test(filename) ? filename : `${filename}${extension}`;
  const saveButton = (
    <Button
      component="a"
      href={imageUrl || undefined}
      download={downloadFilename}
      disabled={loading || failed || !imageUrl}
      size="small"
      startIcon={<Download />}
      sx={{ color: isOutbound && !open ? 'inherit' : '#166534' }}
    ><T>
      Bild speichern
    </T></Button>
  );

  return (
    <Box sx={{ width: 240, maxWidth: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      {loading ? (
        <Box role="status" sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="caption"><T>Bild wird geladen…</T></Typography>
        </Box>
      ) : failed ? (
        <Alert severity="warning" sx={{ alignItems: 'center' }}><T>
          Bild konnte nicht geladen werden.
          </T><Button onClick={() => setAttempt((value) => value + 1)} size="small"><T>Erneut laden</T></Button>
        </Alert>
      ) : (
        <Box
          component="button"
          type="button"
          aria-label={`${filename} vergrössern`}
          onClick={() => setOpen(true)}
          sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', cursor: 'zoom-in' }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={filename}
            onError={() => setFailed(true)}
            sx={{ display: 'block', width: '100%', minHeight: 80, maxHeight: 260, objectFit: 'contain' }}
          />
        </Box>
      )}
      <Box sx={{ px: 0.5 }}>{saveButton}</Box>
      <Dialog open={open && !failed} onClose={() => setOpen(false)} fullWidth maxWidth="lg" aria-labelledby={`image-title-${messageId}-${attachmentIndex}`}>
        <DialogTitle id={`image-title-${messageId}-${attachmentIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ flex: 1, overflowWrap: 'anywhere' }}>{filename}</Typography>
          <IconButton aria-label="Bild schliessen" onClick={() => setOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', bgcolor: '#f8fafc' }}>
          <Box component="img" src={imageUrl} alt={filename} sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
        </DialogContent>
        <DialogActions>{saveButton}<Button onClick={() => setOpen(false)}><T>Schliessen</T></Button></DialogActions>
      </Dialog>
    </Box>
  );
}

ChatImageAttachment.propTypes = {
  attachment: PropTypes.object.isRequired,
  messageId: PropTypes.string.isRequired,
  attachmentIndex: PropTypes.number.isRequired,
  isOutbound: PropTypes.bool,
};
